// StatementsPage — Account statements / Tax center
// Download monthly statements, P&L summaries, tax documents
// Layout: hero w/ YTD totals, year selector, monthly grid, downloads list

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartnerLogo } from '../../components/Brand';

interface MonthlyStatement {
  month: string;
  year: number;
  trades: number;
  netPnl: number;
  fees: number;
  volume: number;
  status: 'ready' | 'pending';
}

const YTD_TOTALS = {
  netPnl: 18420.50,
  trades: 412,
  winRate: 0.67,
  fees: 184.00,
  volume: 1_240_000,
  bestMonth: 'August',
  bestMonthPnl: 4280.00,
};

const STATEMENTS: MonthlyStatement[] = [
  { month: 'June',   year: 2026, trades: 87, netPnl: 2920.50, fees: 79.00, volume: 240_000, status: 'ready' },
  { month: 'May',    year: 2026, trades: 92, netPnl: 4180.00, fees: 79.00, volume: 285_000, status: 'ready' },
  { month: 'April',  year: 2026, trades: 76, netPnl: 1240.00, fees: 79.00, volume: 195_000, status: 'ready' },
  { month: 'March',  year: 2026, trades: 84, netPnl: 3680.00, fees: 79.00, volume: 260_000, status: 'ready' },
  { month: 'February',year: 2026,trades: 73, netPnl: 2120.00, fees: 79.00, volume: 220_000, status: 'ready' },
  { month: 'January',year: 2026, trades: 0,  netPnl: 4280.00, fees: 79.00, volume: 0,       status: 'ready' },
];

export function StatementsPage() {
  const nav = useNavigate();
  const [year, setYear] = useState(2026);
  const [downloading, setDownloading] = useState<string | null>(null);

  function download(s: MonthlyStatement) {
    setDownloading(s.month);
    setTimeout(() => { alert(`Downloaded ${s.month} ${s.year} statement (PDF, 142 KB)`); setDownloading(null); }, 1200);
  }

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Statements & tax</div>
        <button className="px-3 text-[15px] font-semibold text-tf-blue">Export</button>
      </div>

      {/* Hero — YTD */}
      <div className="mx-4 mt-4">
        <div className="ios26-hero text-center" style={{ background: 'linear-gradient(135deg, #5856D6 0%, #1C1C1E 100%)', color: 'white' }}>
          <div className="text-[11px] uppercase tracking-wider text-white/80">Year-to-date</div>
          <div className="mt-1 text-[36px] font-bold tabular-nums">${YTD_TOTALS.netPnl.toLocaleString()}</div>
          <div className="mt-0.5 text-[13px] text-white/80">net P&L in {year}</div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="rounded-[12px] bg-white/15 backdrop-blur-md p-2.5">
              <div className="text-[10px] text-white/70 uppercase">Trades</div>
              <div className="text-[16px] font-bold tabular-nums">{YTD_TOTALS.trades}</div>
            </div>
            <div className="rounded-[12px] bg-white/15 backdrop-blur-md p-2.5">
              <div className="text-[10px] text-white/70 uppercase">Win rate</div>
              <div className="text-[16px] font-bold tabular-nums">{(YTD_TOTALS.winRate * 100).toFixed(0)}%</div>
            </div>
            <div className="rounded-[12px] bg-white/15 backdrop-blur-md p-2.5">
              <div className="text-[10px] text-white/70 uppercase">Volume</div>
              <div className="text-[16px] font-bold tabular-nums">${(YTD_TOTALS.volume / 1000).toFixed(0)}K</div>
            </div>
          </div>
        </div>
      </div>

      {/* Year selector */}
      <div className="mx-4 mt-3 ios26-seg" data-testid="year-toggle">
        {[2026, 2025].map((y) => (
          <button key={y} onClick={() => setYear(y)} className={y === year ? 'active' : ''}>{y}</button>
        ))}
      </div>

      {/* Quick stats */}
      <div className="mx-4 mt-3 grid grid-cols-2 gap-2">
        <QuickStat label="Best month" value={YTD_TOTALS.bestMonth} sub={`+$${YTD_TOTALS.bestMonthPnl.toLocaleString()}`} color="#34C759" />
        <QuickStat label="Fees paid" value={`$${YTD_TOTALS.fees.toFixed(2)}`} sub={`${STATEMENTS.reduce((s, m) => s + m.fees, 0)} invoices`} />
      </div>

      {/* Monthly grid */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Monthly statements</h2>
      <div className="mx-4 grid grid-cols-3 gap-2">
        {STATEMENTS.map((s) => (
          <button
            key={s.month}
            onClick={() => download(s)}
            data-testid={`stmt-${s.month}`}
            className="ios26-card p-3 text-left active:scale-[0.97] transition"
          >
            <div className="text-[10px] uppercase tracking-wider text-tf-text-3">{s.month}</div>
            <div className={['text-[14px] font-bold tabular-nums', s.netPnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
              {s.netPnl >= 0 ? '+' : ''}${Math.abs(s.netPnl).toLocaleString()}
            </div>
            <div className="text-[10px] text-tf-text-3">{s.trades} trades</div>
            <div className="mt-1 flex items-center justify-between">
              {downloading === s.month ? (
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 animate-spin text-tf-blue" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4" strokeLinecap="round" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-tf-blue" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 4v12M5 12l7 7 7-7M4 20h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Tax documents */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Tax documents</h2>
      <div className="mx-4 ios26-list bg-white">
        {[
          { name: '1099-MISC (2025)',      sub: 'Available Jan 31',   icon: '📄', status: 'pending' as const },
          { name: 'Annual P&L Summary',    sub: 'Generated Feb 1',    icon: '📊', status: 'pending' as const },
          { name: 'Cost basis report',     sub: 'For tax filing',     icon: '📑', status: 'pending' as const },
          { name: 'Proceeds (Form 1099-B)', sub: 'Brokerage',         icon: '📋', status: 'pending' as const },
        ].map((doc, i, a) => (
          <button key={doc.name} data-testid={`tax-${i}`} className={['flex w-full items-center gap-3 px-4 py-3 text-left active:bg-tf-surface-1 transition', i < a.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#FF9500]/15 text-[18px]">{doc.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-tf-text-1">{doc.name}</div>
              <div className="text-[11px] text-tf-text-3">{doc.sub}</div>
            </div>
            <span className="rounded-full bg-[#FF9500]/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-tf-warning">Soon</span>
          </button>
        ))}
      </div>

      <p className="mt-3 px-4 text-center text-[10px] text-tf-text-3">Tax docs available Jan 31 · Powered by <PartnerLogo name="stripe" height={11} className="inline-block align-middle" /></p>
    </div>
  );
}

function QuickStat({ label, value, sub, color }: { label: string; value: string; sub: string; color?: string }) {
  return (
    <div className="rounded-[12px] border border-tf-border bg-white p-3">
      <div className="text-[11px] uppercase tracking-wide text-tf-text-3">{label}</div>
      <div className="mt-1 text-[18px] font-bold tabular-nums" style={{ color: color || '#1C1C1E' }}>{value}</div>
      <div className="text-[10px] text-tf-text-3">{sub}</div>
    </div>
  );
}
